import { AdminPageCabecalhoComponent } from '../../shared/admin-page-cabecalho/admin-page-cabecalho.component';
import { Component, inject, ChangeDetectorRef } from '@angular/core';
import { GruposApiService, ListRetrieveGrupo, Permissao, PermissoesApiService } from '@cei-ufg/ngx-cei-seguranca';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import {
  CeiAutocompleteChipsComponent,
  CeiAutocompleteOptionComponent,
  CeiButtonComponent,
  CeiInputTextComponent,
  CeiPanelComponent,
  CeiSnackBarService,
} from '@cei-ufg/ngx-arquitetura-cei-frontend';
import { notifyAdminFormError, notifyAdminFormSuccess, stopIfAdminFormInvalid } from '../../shared/admin-form.utils';
import { forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

/** Edição de grupo com permissões via autocomplete com chips. */
@Component({
  selector: 'cei-admin-grupo-edit',
  standalone: true,
  imports: [AdminPageCabecalhoComponent, 
    CommonModule,
    FormsModule,
    RouterModule,
    CeiInputTextComponent,
    CeiAutocompleteChipsComponent,
    CeiAutocompleteOptionComponent,
    CeiButtonComponent,
    CeiPanelComponent,
  ],
  templateUrl: './edit.component.html',
})
export class AdminGrupoEditComponent {
  form: Partial<ListRetrieveGrupo> = { name: '' };
  availablePermissions: Permissao[] = [];
  permissoesSelecionadas: Permissao[] = [];
  permissoesIniciaisIds: number[] = [];
  permissoesCatalogoCarregado = false;
  /** Incrementado após carregar o grupo; força montagem do autocomplete com chips do backend. */
  formVersao = 0;

  gruposApi = inject(GruposApiService);
  permissoesApi = inject(PermissoesApiService);
  route = inject(ActivatedRoute);
  cdr = inject(ChangeDetectorRef);
  private readonly snackBar = inject(CeiSnackBarService);

  get registroId(): number {
    return Number(this.route.snapshot.paramMap.get('id'));
  }

  /** Rótulo de opção e filtro de busca de permissão (descrição + slug). */
  protected readonly formatarPermissao = (valor: unknown): string => {
    const permissao = valor as Permissao;
    return `${permissao.name} (${permissao.codename})`;
  };

  /** Rótulo do chip de permissão (somente slug/codename). */
  protected readonly formatarPermissaoChip = (valor: unknown): string =>
    (valor as Permissao).codename;

  /** Chave estável para deduplicar permissões selecionadas. */
  protected readonly extrairChavePermissao = (valor: unknown): string =>
    String((valor as Permissao).id);

  ngOnInit(): void {
    this.carregarPermissoesDisponiveis();

    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      return;
    }

    this.gruposApi.grupoRetrieve({ id }).subscribe((data) => {
      this.aplicarGrupoCarregado(data);
      this.cdr.markForCheck();
    });
  }

  /** Chave de remontagem do autocomplete (catálogo + seleção vinda do backend). */
  chaveAutocompletePermissoes(): string {
    const ids = this.permissoesSelecionadas.map((permissao) => permissao.id).join('|');
    return `${this.formVersao}|${this.permissoesCatalogoCarregado}|${ids}`;
  }

  onSubmit(form: NgForm) {
    if (stopIfAdminFormInvalid(form)) {
      this.cdr.markForCheck();
      return;
    }

    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      notifyAdminFormError(this.snackBar, 'Erro ao atualizar grupo.');
      this.cdr.markForCheck();
      return;
    }

    const permissoesSelecionadasIds = this.permissoesSelecionadas.map((permissao) =>
      Number(permissao.id),
    );

    this.gruposApi
      .grupoUpdate({ id, putGrupo: this.form as ListRetrieveGrupo })
      .pipe(
        switchMap(() => {
          const toAdd = permissoesSelecionadasIds.filter(
            (permissaoId) => !this.permissoesIniciaisIds.includes(permissaoId),
          );
          const toRemove = this.permissoesIniciaisIds.filter(
            (permissaoId) => !permissoesSelecionadasIds.includes(permissaoId),
          );

          const associationCalls = toAdd.map((permissaoId) =>
            this.gruposApi.grupoAssociarPermissaoCreate({
              id,
              associateGrupoPermissao: { permissao: permissaoId },
            }),
          );

          const removalCalls = toRemove.map((permissaoId) =>
            this.removerPermissaoDoGrupo(id, permissaoId),
          );

          const syncCalls = [...associationCalls, ...removalCalls];
          return syncCalls.length > 0 ? forkJoin(syncCalls) : of([]);
        }),
        switchMap(() => this.gruposApi.grupoRetrieve({ id })),
      )
      .subscribe({
        next: (data) => {
          this.aplicarGrupoCarregado(data);
          notifyAdminFormSuccess(this.snackBar, 'Grupo atualizado com sucesso!');
          this.cdr.markForCheck();
        },
        error: (err) => {
          notifyAdminFormError(this.snackBar, 'Erro ao atualizar grupo.', err);
          this.cdr.markForCheck();
        },
      });
  }

  /** Sincroniza formulário e chips com o grupo retornado pela API. */
  private aplicarGrupoCarregado(data: ListRetrieveGrupo): void {
    this.form = { ...data };
    this.permissoesIniciaisIds = (data.permissions ?? []).map((permissao) => Number(permissao.id));
    this.permissoesSelecionadas = [...(data.permissions ?? [])];
    this.formVersao++;
  }

  /** Carrega catálogo de permissões para o autocomplete de chips. */
  private carregarPermissoesDisponiveis(): void {
    this.permissoesApi.permissaoList({}).subscribe({
      next: (data) => {
        this.availablePermissions = data.results ?? [];
        this.permissoesCatalogoCarregado = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.availablePermissions = [];
        this.permissoesCatalogoCarregado = true;
        this.cdr.markForCheck();
      },
    });
  }

  /** Remove permissão do grupo via cliente OpenAPI (DELETE com body). */
  private removerPermissaoDoGrupo(grupoId: number, permissaoId: number) {
    return this.gruposApi.grupoRemoverPermissaoDestroy({
      id: grupoId,
      associateGrupoPermissao: { permissao: permissaoId },
    });
  }
}
